import { faPlus, faTrash } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Box,
  Button,
  Stack,
  Typography,
} from "@mui/joy";
import { useSnackbar } from "notistack";
import {
  memo,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import {
  type AudienceSeedOption,
  type DraftRuleBasedAudience,
  RuleBasedAudienceCombinatorOperator,
  type RuleBasedAudienceFilter,
  RuleBasedAudienceFiltersBooleanOperator,
  RuleBasedAudienceSchema,
} from "features/mediaDataRoom/models";
import {
  AddAudienceButton,
  AudienceFilter,
  AudienceSelector,
  AudienceSwitch,
  CombinatorChip,
} from "./components";

export interface RuleBasedAudienceBuilderProps {
  seedOptions: AudienceSeedOption[];
  defaultValue?: DraftRuleBasedAudience;
  onChange: (value: DraftRuleBasedAudience) => void;
}

const RuleBasedAudienceBuilder = memo<RuleBasedAudienceBuilderProps>(
  ({ seedOptions, defaultValue, onChange }) => {
    // TODO: Temp fix to show messages in snackbar, should be removed when builder gets into form shape
    const { enqueueSnackbar } = useSnackbar();
    const [rulesTree, setRulesTree] = useState<DraftRuleBasedAudience>(
      defaultValue || {
        combine: [],
        filters: undefined,
        name: "",
        sourceRef: null,
      }
    );
    const [expandedAccordions, setExpandedAccordions] = useState(new Set([0]));
    const isValidTree = useMemo(() => {
      return RuleBasedAudienceSchema.safeParse({
        ...rulesTree,
        id: "draft_id",
      });
    }, [rulesTree]);
    const handleTreeChange = useCallback(
      (valueOrSetter: SetStateAction<DraftRuleBasedAudience>) => {
        if (typeof valueOrSetter === "function") {
          return setRulesTree((currentState) => {
            const nextState = valueOrSetter(currentState);
            onChange(nextState);
            return nextState;
          });
        } else {
          return setRulesTree(() => {
            onChange(valueOrSetter);
            return valueOrSetter;
          });
        }
      },
      [onChange]
    );

    const handleDefaultAudienceSelect = useCallback(
      (audience: AudienceSeedOption | null) => {
        handleTreeChange({
          combine: null,
          filters: null,
          ...(audience
            ? { name: audience.name, sourceRef: audience.id }
            : { name: "", sourceRef: "" }),
        });
      },
      [handleTreeChange]
    );

    const handleCombineAudienceSelect = (
      audience: AudienceSeedOption | null,
      combineIndex: number
    ) => {
      handleTreeChange((prev) => ({
        ...prev,
        combine: prev.combine?.map((combine, ci) =>
          ci === combineIndex
            ? {
                ...combine,
                name: audience?.name || "",
                sourceRef: audience?.id || "",
              }
            : combine
        ),
      }));
    };

    const handleFilterAdd = (combineIndex?: number) => {
      handleTreeChange((prev) =>
        combineIndex !== undefined
          ? {
              ...prev,
              combine: prev.combine?.map((combine, ci) =>
                ci === combineIndex
                  ? {
                      ...combine,
                      filters: {
                        filters: [
                          ...(combine.filters?.filters || []),
                          {
                            attribute: null,
                            condition: null,
                            values: [],
                          },
                        ],
                        operator:
                          combine.filters?.operator ||
                          RuleBasedAudienceFiltersBooleanOperator.AND,
                      },
                    }
                  : combine
              ),
            }
          : {
              ...prev,
              filters: {
                filters: [
                  ...(prev.filters?.filters || []),
                  {
                    attribute: null,
                    condition: null,
                    values: [],
                  },
                ],
                operator:
                  prev.filters?.operator ||
                  RuleBasedAudienceFiltersBooleanOperator.AND,
              },
            }
      );
    };

    const handleCombineDelete = (
      event: React.MouseEvent<SVGSVGElement, MouseEvent>,
      combineIndex: number
    ) => {
      event.stopPropagation();
      handleTreeChange((prev) => ({
        ...prev,
        combine:
          combineIndex === 0 && prev.combine?.length === 1
            ? null
            : prev.combine?.filter((_, index) => index !== combineIndex),
      }));
      setExpandedAccordions(
        (currenState) =>
          new Set([...currenState].filter((i) => i !== combineIndex + 1))
      );
    };

    /**
     * Adds an audience to the rules tree.
     *
     * @param operator - The value of the audience combinator operator.
     */
    const handleAudienceAdd = (
      operator: RuleBasedAudienceCombinatorOperator
    ) => {
      if (isValidTree.error) {
        return enqueueSnackbar(
          "Please fill the all necessary attributes, conditions and values to proceed",
          {
            persist: true,
            variant: "info",
          }
        );
      } else {
        setExpandedAccordions(
          (currentState) =>
            new Set([...currentState, (rulesTree.combine?.length ?? 0) + 1])
        );
        handleTreeChange((prev) => ({
          ...prev,
          combine: [
            ...(prev.combine || []),
            {
              filters: null,
              name: "",
              operator,
              sourceRef: null,
            },
          ],
        }));
      }
    };

    const availableCombineOperators = useMemo(() => {
      if (rulesTree?.combine && rulesTree.combine.length > 0) {
        if (
          rulesTree.combine[0].operator ===
          RuleBasedAudienceCombinatorOperator.UNION
        ) {
          return [RuleBasedAudienceCombinatorOperator.UNION];
        }
        if (
          rulesTree.combine[0].operator ===
          RuleBasedAudienceCombinatorOperator.INTERSECT
        ) {
          return [RuleBasedAudienceCombinatorOperator.INTERSECT];
        }
        if (
          rulesTree.combine[0].operator ===
          RuleBasedAudienceCombinatorOperator.DIFF
        ) {
          return [];
        }
      }
      return [
        RuleBasedAudienceCombinatorOperator.INTERSECT,
        RuleBasedAudienceCombinatorOperator.UNION,
        RuleBasedAudienceCombinatorOperator.DIFF,
      ];
    }, [rulesTree?.combine]);

    const handleDeleteFilter = useMemo(
      () => (filterIndex: number, combineIndex?: number) => {
        handleTreeChange((prev) =>
          combineIndex !== undefined
            ? {
                ...prev,
                combine: prev.combine?.map((combine, ci) =>
                  ci === combineIndex && combine.filters
                    ? {
                        ...combine,
                        filters:
                          filterIndex === 0 &&
                          combine.filters.filters.length === 1
                            ? null
                            : {
                                ...combine.filters,
                                filters: combine.filters?.filters?.filter(
                                  (_, fi) => fi !== filterIndex
                                ),
                              },
                      }
                    : combine
                ),
              }
            : {
                ...prev,
                filters: prev.filters
                  ? prev.filters.filters.length === 1 && filterIndex === 0
                    ? null
                    : {
                        ...prev.filters,
                        filters: prev.filters?.filters?.filter(
                          (_, fi) => fi !== filterIndex
                        ),
                      }
                  : prev.filters,
              }
        );
      },
      [handleTreeChange]
    );

    const handleFilterOperatorChange = (
      checked: boolean,
      combineIndex?: number
    ) => {
      handleTreeChange((prev) =>
        combineIndex !== undefined
          ? {
              ...prev,
              combine: prev.combine?.map((combine, ci) =>
                ci === combineIndex && combine.filters
                  ? {
                      ...combine,
                      filters: {
                        ...combine.filters,
                        operator: checked
                          ? RuleBasedAudienceFiltersBooleanOperator.AND
                          : RuleBasedAudienceFiltersBooleanOperator.OR,
                      },
                    }
                  : combine
              ),
            }
          : {
              ...prev,
              filters: prev.filters
                ? {
                    ...prev.filters,
                    operator: checked
                      ? RuleBasedAudienceFiltersBooleanOperator.AND
                      : RuleBasedAudienceFiltersBooleanOperator.OR,
                  }
                : prev.filters,
            }
      );
    };

    const handleFilterChange = useCallback(
      (
        filter: RuleBasedAudienceFilter,
        filterIndex: number,
        combineIndex?: number
      ) => {
        handleTreeChange((prev) =>
          combineIndex !== undefined
            ? {
                ...prev,
                combine: prev.combine?.map((combine, ci) =>
                  ci === combineIndex && combine.filters
                    ? {
                        ...combine,
                        filters: {
                          ...combine.filters,
                          filters: combine.filters?.filters?.map((_, fi) =>
                            fi === filterIndex ? filter : _
                          ),
                        },
                      }
                    : combine
                ),
              }
            : {
                ...prev,
                filters: prev.filters
                  ? {
                      ...prev.filters,
                      filters: prev.filters?.filters?.map((_, fi) =>
                        fi === filterIndex ? filter : _
                      ),
                    }
                  : prev.filters,
              }
        );
      },
      [handleTreeChange]
    );

    return (
      <Box width="100%">
        <AccordionGroup>
          <Accordion
            data-testid="default-audience-selector"
            defaultExpanded={true}
            expanded={expandedAccordions.has(0)}
            onChange={(_, expanded) =>
              setExpandedAccordions((prev) =>
                expanded
                  ? prev.union(new Set([0]))
                  : prev.difference(new Set([0]))
              )
            }
          >
            <AccordionSummary>Audience 1</AccordionSummary>
            <AccordionDetails sx={{ "--ListItem-paddingY": 0 }}>
              <AudienceSelector
                chipText="Users who belong to: "
                onSelect={handleDefaultAudienceSelect}
                seedOptions={seedOptions}
                testId="default-audience-select"
                value={rulesTree?.sourceRef ?? null}
              />
              <Typography sx={{ m: 1 }}>With the following filters:</Typography>
              {rulesTree?.filters?.filters.length ? (
                <Box
                  sx={(theme) => ({
                    backgroundColor: theme.palette.primary[100],
                    borderRadius: theme.radius.sm,
                    px: 1,
                    py: 2,
                  })}
                >
                  {rulesTree?.filters?.filters.map(
                    ({ condition, attribute, values }, filterIndex) => (
                      <Box key={filterIndex}>
                        {rulesTree?.filters?.filters &&
                          filterIndex !== 0 &&
                          rulesTree.filters.filters.length > 1 && (
                            <AudienceSwitch
                              checked={
                                rulesTree.filters.operator ===
                                RuleBasedAudienceFiltersBooleanOperator.AND
                              }
                              onChange={(e) =>
                                handleFilterOperatorChange(e.target.checked)
                              }
                            />
                          )}
                        <AudienceFilter
                          attribute={attribute}
                          attributes={
                            seedOptions.find(
                              ({ id }) => id === rulesTree.sourceRef
                            )?.attributes || {}
                          }
                          condition={condition}
                          handleRemove={() => handleDeleteFilter(filterIndex)}
                          onChange={(filter) =>
                            handleFilterChange(filter, filterIndex)
                          }
                          values={values}
                        />
                      </Box>
                    )
                  )}
                </Box>
              ) : null}
              <Box>
                <Button
                  color="primary"
                  onClick={() => handleFilterAdd()}
                  startDecorator={
                    <FontAwesomeIcon fixedWidth={true} icon={faPlus} />
                  }
                  sx={{ my: 1 }}
                  variant="plain"
                >
                  Add a new filter
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        </AccordionGroup>

        {rulesTree?.combine?.map((combine, combineIndex) => {
          const { operator, sourceRef } = combine;
          return (
            <Box key={combineIndex}>
              {operator && (
                <Stack
                  direction="row"
                  justifyContent={"center"}
                  spacing={1}
                  width="100%"
                >
                  <CombinatorChip operator={operator} />
                </Stack>
              )}
              <AccordionGroup>
                <Accordion
                  defaultExpanded={true}
                  expanded={expandedAccordions.has(combineIndex + 1)}
                  onChange={(_, expanded) =>
                    setExpandedAccordions((prev) =>
                      expanded
                        ? prev.union(new Set([combineIndex + 1]))
                        : prev.difference(new Set([combineIndex + 1]))
                    )
                  }
                >
                  <AccordionSummary>
                    <Stack
                      alignItems="center"
                      direction="row"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <Typography>Audience {combineIndex + 2}</Typography>
                      <FontAwesomeIcon
                        icon={faTrash}
                        onClick={(e) => handleCombineDelete(e, combineIndex)}
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ "--ListItem-paddingY": 0 }}>
                    <AudienceSelector
                      chipText="Users who belong to: "
                      onSelect={(audience) =>
                        handleCombineAudienceSelect(audience, combineIndex)
                      }
                      seedOptions={seedOptions}
                      value={sourceRef}
                    />
                    <Typography sx={{ m: 1 }}>
                      With the following filters:
                    </Typography>
                    {combine.filters?.filters.length ? (
                      <Box
                        sx={(theme) => ({
                          backgroundColor: theme.palette.primary[100],
                          borderRadius: theme.radius.sm,
                          p: 1,
                        })}
                      >
                        {combine.filters?.filters.map(
                          ({ attribute, condition, values }, filterIndex) => (
                            <Box
                              key={`${combineIndex}-${filterIndex}-${sourceRef}`}
                            >
                              {filterIndex !== 0 && (
                                <AudienceSwitch
                                  checked={
                                    combine.filters?.operator ===
                                    RuleBasedAudienceFiltersBooleanOperator.AND
                                  }
                                  onChange={(e) =>
                                    handleFilterOperatorChange(
                                      e.target.checked,
                                      combineIndex
                                    )
                                  }
                                />
                              )}
                              <AudienceFilter
                                attribute={attribute}
                                attributes={
                                  seedOptions.find(({ id }) => id === sourceRef)
                                    ?.attributes || {}
                                }
                                condition={condition}
                                handleRemove={() =>
                                  handleDeleteFilter(filterIndex, combineIndex)
                                }
                                onChange={(filter) =>
                                  handleFilterChange(
                                    filter,
                                    filterIndex,
                                    combineIndex
                                  )
                                }
                                values={values}
                              />
                            </Box>
                          )
                        )}
                      </Box>
                    ) : null}
                    <Box>
                      <Button
                        color="primary"
                        onClick={() => handleFilterAdd(combineIndex)}
                        startDecorator={
                          <FontAwesomeIcon fixedWidth={true} icon={faPlus} />
                        }
                        sx={{ my: 1 }}
                      >
                        Add a new filter
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </AccordionGroup>
            </Box>
          );
        })}
        <AddAudienceButton
          combinatorOperators={availableCombineOperators}
          handleAddAudience={handleAudienceAdd}
        />
      </Box>
    );
  }
);
RuleBasedAudienceBuilder.displayName = "RuleBasedAudienceBuilder";

export default RuleBasedAudienceBuilder;
